<script lang="ts">
    import '../app.postcss';
    import {
        type DrawerSettings,
        getDrawerStore,
        initializeStores,
        Modal,
        type ModalComponent,
        storePopup,
        Toast
    } from '@skeletonlabs/skeleton';

    // Floating UI for Popups
    import {arrow, autoUpdate, computePosition, flip, offset, shift} from '@floating-ui/dom';
    import ClientSsoModal from '$lib/components/modals/ClientSsoModal.svelte';
    import ClientContractModal from '$lib/components/modals/ClientContractModal.svelte';
    import ReckonResponseModal from '$lib/components/modals/ReckonResponseModal.svelte';
    import TassInvoicePostModal from '$lib/components/modals/TassInvoicePostModal.svelte';
  import ForceSyncConfirmModal from '$lib/components/support-tools/ForceSyncConfirmModal.svelte';
    import ExportMetaDataModal from '$lib/components/modals/ExportMetaDataModal.svelte';
    import ExportFileListModal from '$lib/components/modals/ExportFileListModal.svelte';
    import SharepointV3CertifcateModal
        from '$lib/components/drawers/client/sharepoint/SharepointV3CertifcateModal.svelte';
    import DeleteDetailsModal from '$lib/components/modals/DeleteDetailsModal.svelte';
    import Navbar from '$lib/components/navbar/Navbar.svelte';
    import {List} from 'phosphor-svelte';
    import Login from '$lib/components/Login.svelte';
    import {page} from '$app/stores';
    import {title} from '$lib/stores/navStore';
    import {authStore} from '$lib/stores/authStore';
    import {onMount} from 'svelte';
    import BaseDrawer, {DrawerType} from '$lib/components/drawers/BaseDrawer.svelte';
    import {initializeToast} from '$lib/helpers/toastrHelper';
    import {loadRegion, region} from '$lib/stores/regionStore';
    import {loadSettings} from '$lib/stores/settingsStore';

    storePopup.set({ computePosition, autoUpdate, offset, shift, flip, arrow });
  initializeStores();
  initializeToast();

  const modalComponentRegistry: Record<string, ModalComponent> = {
    ClientSsoModal: { ref: ClientSsoModal },
    ClientContractModal: { ref: ClientContractModal },
    reckonResponseModal: { ref: ReckonResponseModal },
    tassInvoicePostModal: { ref: TassInvoicePostModal },
    forceSyncConfirmModal: { ref: ForceSyncConfirmModal },
    exportMetaDataModal: { ref: ExportMetaDataModal },
    exportFileListModal: { ref: ExportFileListModal },
    sharepointV3CertifcateModal: { ref: SharepointV3CertifcateModal },
    deleteDetailsModal: { ref: DeleteDetailsModal },
  };

  const drawer = getDrawerStore();

  function drawerOpen(): void {
    const navbarDrawer: DrawerSettings = {
      id: DrawerType.Navigation,
    };
    drawer.open(navbarDrawer);
  }

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { width } = entry.contentRect;
      if (width > 1024) {
        const { open, id } = $drawer;
        if (open && id === DrawerType.Navigation) {
          drawer.close();
        }
      }
    }
  });

  window.addEventListener('resize', () => {
    resizeObserver.observe(document.body);
  });

  $: pageTitle = $title;
  const isLoading = authStore.isLoading;

  const user = authStore;

  $: isCallback = $page.url.pathname === '/oidc-callback' || $page.url.pathname === '/oidc-callback/';

  // Load region and app settings once when the user becomes authenticated.
  $: if ($user) loadRegion();
  $: if ($user) loadSettings();

  onMount(async () => {
    // The /callback route completes the login itself; don't race it with init().
    if (!isCallback) {
      await authStore.init();
    }
  });

  function getInitials(name: string): string {
    return name
      .split(/\s+/)
      .map((word) => word[0].toUpperCase())
      .join('')
      .slice(0, 2);
  }

  $: userInitials = $user ? getInitials($user.profile?.name || '') : '';
</script>

<head>
  <title>{pageTitle}</title>
</head>
<Toast zIndex="z-[1000]" />
<Modal components={modalComponentRegistry} />
{#if isCallback}
  <slot />
{:else if $isLoading}
  <div class="flex h-screen items-center justify-center">
    <p>Loading...</p>
  </div>
{:else if $user}
  <BaseDrawer />
  <!-- App -->
  <div id="app" class="grid grid-cols-1 lg:grid-cols-[auto_1fr]">
    <!-- Header -->
    <header class="bg-white lg:hidden lg:w-0">
      {#if $region}
        <div class="bg-[#081E59] px-4 py-1.5 text-xs font-medium text-white text-right">
          Region: <span class="font-bold">{$region}</span>
        </div>
      {/if}
      <div class="flex items-center p-4">
        <button class="btn btn-sm mr-4 lg:hidden" on:click={drawerOpen}>
          <List />
        </button>
        <img src="/alii-logo.svg" alt="Alii Logo" class="w-20" />
      </div>
    </header>
    <!-- Grid Columns -->
    <div class="col-span-full grid h-full grid-cols-1 gap-5 lg:grid-cols-[auto_1fr]">
      <!-- Left Sidebar. -->
      <aside class="sticky top-0 col-span-1 hidden h-screen w-0 flex-col border-r-2 bg-white lg:flex lg:w-72">
        {#if $region}
          <div class="bg-[#081E59] px-4 py-1.5 text-xs font-medium text-white text-right">
            Region: <span class="font-bold">{$region}</span>
          </div>
        {/if}
        <div class="m-4">
          <img src="/alii-logo.svg" alt="Alii Logo" class="hidden w-20 flex-shrink-0 lg:block" />
        </div>
        <div class="flex-1 overflow-y-auto">
          <Navbar />
        </div>
        {#if $user.profile?.name}
          <div class="flex items-center gap-3 border-t border-slate-200 p-4">
            <div class="flex h-9 w-9 flex-shrink-0 cursor-default items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
              {userInitials}
            </div>
            <span class="truncate text-sm font-medium text-slate-600" title={$user.profile.name}>
              {$user.profile.name}
            </span>
          </div>
        {/if}
      </aside>
      <!-- Main Content -->
      <main class="col-span-1 space-y-4">
        <slot />
      </main>
    </div>
  </div>
{:else}
  <Login></Login>
{/if}
